import React, { useState } from 'react';
import './MembershipProgramSection.css';

export const services = [
  {
    title: "Regular coaching and clinical touchpoints",
    body: "Structured monthly follow-ups and strategy sessions. Accountability without a lecture. The chart stays current, so advice does too.",
  },
  {
    title: "In-hospital advocacy",
    body: "If you need a hospital, you are not navigating it alone. We help coordinate the admission, the questions, and the handoff back to your plan.",
  },
  {
    title: "Annual executive health assessment",
    body: "A full-spectrum, multidisciplinary evaluation of the risk domains that matter, plus a baseline you can compare next year.",
  },
  {
    title: "A dedicated clinical team",
    body: "Physicians, naturopaths, physiologists, and health strategists under one roof. One conversation, not five waiting rooms.",
  },
  {
    title: "Global health support",
    body: "Medical coordination wherever work or family takes you. The membership travels. You should not have to re-explain your history in every city.",
  },
];

function MembershipProgramSection({
  bgImage = "/images/creatives/membership-program-bg.jpg",
  tagline = "THE MEMBERSHIP PROGRAM",
  headingLine1 = "A comprehensive",
  headingLine2 = "program designed ",
  headingItalic = "to empower you.",
  description = "The Toronto Clinic delivers more than healthcare. It delivers a strategy for a life — precision, personalization, and prevention, held by one team from every angle.",
  consultationLink = "/book-a-consultation/membership",
  servicesList = services
}) {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="ttc-membership-section py-5">
      <div className="container py-lg-3">
        
        {/* Top Hero Banner */}
        <div 
          className="ttc-membership-hero position-relative overflow-hidden p-4 p-md-5 mb-4 text-white"
          style={{ backgroundImage: `linear-gradient(rgba(10, 16, 26, 0.65), rgba(10, 16, 26, 0.65)), url(${bgImage})` }}
        >
          <div className="row align-items-center position-relative z-1">
            <div className="col-12 col-lg-8 pe-lg-5">
              
              {/* Tagline */}
              <div className="ttc-membership-tagline text-uppercase mb-3 d-flex align-items-center gap-2">
                <span className="ttc-membership-bullet">•</span>
                <span>{tagline}</span>
              </div>

              {/* Main Heading */}
              <h1 className="ttc-membership-heading mb-4" style={{color:"white"}}>
                {headingLine1} <br />
                {headingLine2}
                <span className="ttc-membership-italic">{headingItalic}</span>
              </h1>

              {/* Subtext Description */}
              <p className="ttc-membership-desc mb-4">
                {description}
              </p>

              {/* Primary Pill Button */}
              <a href={consultationLink} className="ttc-pill-btn d-inline-flex align-items-center gap-3 text-decoration-none">
                <span>Book a consultation</span>
                <span className="ttc-pill-btn-arrow">→</span>
              </a>

            </div>

            {/* Rotating Stamp Button */}
            <div className="col-12 col-lg-4 d-flex justify-content-lg-end justify-content-start mt-4 mt-lg-0">
              <a href={consultationLink} className="ttc-rotating-stamp-wrapper text-decoration-none">
                <div className="ttc-rotating-stamp">
                  <svg viewBox="0 0 100 100" width="120" height="120" className="ttc-stamp-svg">
                    <path
                      id="circlePath"
                      d="M 50, 50 m -38, 0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0"
                      fill="none"
                    />
                    <text className="ttc-stamp-text">
                      <textPath href="#circlePath" startOffset="0%">
                        GET STARTED • GET STARTED • GET STARTED •
                      </textPath>
                    </text>
                  </svg>
                  <div className="ttc-stamp-circle">
                    <span className="ttc-stamp-arrow">→</span>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Accordion Services Grid */}
        <div className="row g-3">
          {servicesList.map((item, index) => {
            const isOpen = openIndex === index;
            const formattedNumber = String(index + 1).padStart(2, '0');

            return (
              <div key={index} className="col-12 col-md-6">
                <div 
                  className={`ttc-accordion-card p-3 p-md-4 ${isOpen ? 'is-open' : ''}`}
                  onClick={() => toggleAccordion(index)}
                  role="button"
                  tabIndex={0}
                >
                  {/* Card Header Header Row */}
                  <div className="d-flex align-items-center justify-content-between gap-3">
                    <div className="d-flex align-items-baseline gap-3">
                      <span className="ttc-accordion-num">{formattedNumber}</span>
                      <h3 className="ttc-accordion-title mb-0">{item.title}</h3>
                    </div>
                    <button className="ttc-accordion-toggle-btn border-0 rounded-circle d-flex align-items-center justify-content-center" type="button">
                      <span className={`ttc-toggle-icon ${isOpen ? 'open' : ''}`}>+</span>
                    </button>
                  </div>

                  {/* Smooth Collapsible Content Body */}
                  <div className={`ttc-accordion-content ${isOpen ? 'show' : ''}`}>
                    <div className="ttc-accordion-inner pt-3 ps-4 ms-2">
                      <p className="ttc-accordion-body text-muted mb-0">
                        {item.body}
                      </p>
                    </div>
                  </div>

                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}

export default MembershipProgramSection;